import { LanguageAvailable } from '@shared/services/language/models';
import { LogsService } from '@shared/services/logs';
import { AvailableSlotsApi, TimeslotError, Timeslots } from '@shared/services/timeslots/models';
import { getLocaleDateFns } from '@shared/utils';
import { format, parse, parseISO } from 'date-fns';

/**
 * Transforms raw slot data from an API response into a more user-friendly timeslot structure.
 *
 * This function processes the input data to create a localized and formatted representation
 * of available timeslots. It handles parsing of dates, formatting labels, and generating start
 * and end date-times in consistent formats. If any error occurs during serialization, it logs
 * the error (if logging is enabled) and returns an empty array.
 *
 * @param {AvailableSlotsApi} slotsData - The raw slot data from the API. It includes the status,
 * slot information, and localized date strings.
 * @param {Pick<LogsService, 'error'>} [logs] - An optional logging service that handles errors.
 * If provided, failures during the serialization process are logged.
 * @returns {Timeslots[] | TimeslotError} A list of formatted timeslot objects with date and time
 * labels if serialization is successful, or an error message (`TimeslotError`) if the input
 * data has an invalid status (`KO`).
 *
 * @see TimeslotsService.getTimeslots, its caller.
 *
 * @throws {Error} Logs an error via the provided logging service if any unexpected issues occur
 * during parsing or formatting of the slots.
 *
 * @remarks
 * - If `slotsData.status` is set to `'KO'`, the function immediately returns the error message
 *   contained in `slotsData.message`.
 * - The function assumes that all input dates and times provided in the `slots` array
 *   are valid ISO 8601 strings.
 */
export const appointmentSerializer = (
  slotsData: AvailableSlotsApi,
  language: LanguageAvailable,
  logs?: Pick<LogsService, 'error'>
): Timeslots[] | TimeslotError => {
  const locale = getLocaleDateFns(language);

  if (slotsData.status === 'KO') {
    return slotsData.message;
  }
  const { slots } = slotsData;

  try {
    return slots.map((day) => {
      const dateObj = parseISO(day.date);
      const dateLabel = format(dateObj, 'EEEE d MMMM', { locale });

      const formattedSlots = day.schedules.map((slot) => {
        const combinedStart = `${day.date} ${slot.start}`; // ex. "2025-06-30 09:00"
        const startDateTime = parse(combinedStart, 'yyyy-MM-dd HH:mm', new Date());
        const startDate = format(startDateTime, 'yyyyMMddHHmm');
        const combinedEnd = `${day.date} ${slot.end}`; // ex. "2025-06-30 11:00"
        const endDateTime = parse(combinedEnd, 'yyyy-MM-dd HH:mm', new Date());
        const endDate = format(endDateTime, 'yyyyMMddHHmm');
        const timeObj = parse(slot.start, 'HH:mm', new Date());
        const slotLabel = format(timeObj, timeObj.getMinutes() === 0 ? "H'h'" : "H'h'mm", { locale });

        return {
          slot: slotLabel,
          startDate,
          endDate,
        };
      });

      return {
        dateLabel,
        formattedSlots,
      };
    });
  } catch (error) {
    logs?.error('appointmentSerializer', 'Error during slot serialization', error);
    return [];
  }
};
